import {Card, DataStatus, GridPhoto, PhotoGrid} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';
import {Lightbox, useLightbox} from 'platform/lightbox';

// eslint-disable-next-line no-restricted-imports
import {useSearchParams} from 'react-router-dom';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetMultipleAuditDataQuery} from '@dms/api/core';
import {PartialVehiclePhotosRoutes} from '@dms/api/shared';
import {
  useGetMetadaServiceCaseQuery,
  useGetServiceCheckInQuery,
  useGetServiceHandOverQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  Main,
  getGridPhotoFullSizeUrl,
  getGridPhotosFromAudit,
  useBatchDownload,
  workshopSearchParams,
  ServiceCasePhotos,
  getGridVideosFromAudit,
  VideoPreview,
  isVideoPlayable,
  getPrecedingPlayableVideos,
  downloadFile,
  Video,
  VehicleAlbumGallery,
  useDmsLightboxActionsFactory,
} from '@dms/shared';

import {Nullish} from 'shared/web';

import {HANDOVER_SECTION} from '../../../../constants/detailSections';
import {useWorkshopUrl} from '../../../../hooks/useWorkshopUrl';

interface PhotosProps {
  isReadOnly?: boolean;
}

export function Photos(_props: PhotosProps) {
  const {serviceCaseId} = useWorkshopUrl();
  const [, setSearchParams] = useSearchParams();

  const {handleDownload} = useBatchDownload();

  const {
    data: serviceCase,
    isLoading: isServiceCaseLoading,
    isError: isServiceCaseError,
  } = useGetMetadaServiceCaseQuery({serviceCaseId});

  const {
    data: checkin,
    isLoading: isCheckinLoading,
    isError: isCheckinError,
  } = useGetServiceCheckInQuery({
    serviceCaseId,
  });
  const {
    data: handover,
    isLoading: isHandoverLoading,
    isError: isHandoverError,
  } = useGetServiceHandOverQuery({
    serviceCaseId,
  });
  const {
    data: checkinAudit,
    isLoading: isCheckinAuditLoading,
    isError: isCheckinAuditError,
  } = useGetMultipleAuditDataQuery(
    {
      auditIds: checkin?.inspectionId ?? [],
    },
    {
      skip: isNilOrEmpty(checkin?.inspectionId),
      refetchOnMountOrArgChange: true,
    }
  );
  const {
    data: handoverAudit,
    isLoading: isHandoverAuditLoading,
    isError: isHandoverAuditError,
  } = useGetMultipleAuditDataQuery(
    {
      auditIds: handover?.inspectionId ?? [],
    },
    {
      skip: isNilOrEmpty(handover?.inspectionId),
      refetchOnMountOrArgChange: true,
    }
  );

  const handleInspectionGalleryBatchDownload = (auditId?: string) =>
    handleDownload({
      vehiclePhotoLocation: PartialVehiclePhotosRoutes.inspections,
      vehicleId: serviceCase?.vehicleId ?? '',
      auditId,
    });

  const checkinPhotos = getGridPhotosFromAudit(checkinAudit?.flatMap((audit) => audit.categories));
  const checkinVideos = getGridVideosFromAudit(checkinAudit?.flatMap((audit) => audit.categories));
  const handoverPhotos = getGridPhotosFromAudit(
    handoverAudit?.flatMap((audit) => audit.categories)
  );
  const handoverVideos = getGridVideosFromAudit(
    handoverAudit?.flatMap((audit) => audit.categories)
  );

  const [checkinPhotosLightboxControls, {onOpen: onCheckinPhotoOpen}] =
    useLightbox('checkinPhotos');
  const [handoverPhotosLightboxControls, {onOpen: onHandoverPhotoOpen}] =
    useLightbox('handoverPhotos');
  const lightboxActionsFactory = useDmsLightboxActionsFactory();

  const makeLightboxPhotos = (photos: GridPhoto[] | Nullish) =>
    lightboxActionsFactory.addFullActions(
      photos?.map((image) => ({
        id: image.id,
        url: getGridPhotoFullSizeUrl(image),
        resizeUrl: image.resizeUrl,
        title: image.title,
        fileName: image.title || image.id ? `${image.title || image.id}.jpg` : undefined,
      }))
    );
  const makeLightboxVideos = (videos: Video[] | Nullish) =>
    lightboxActionsFactory.addShareOnlyActions(videos?.filter(isVideoPlayable), (video) => [
      {
        id: 'downloadBtn',
        icon: 'file/download',
        onClick: () => {
          if (isNotNil(video.originUrl)) {
            downloadFile(video.originUrl);
          }
        },
      },
    ]);

  const isServiceCaseVehicleLoading = isServiceCaseLoading;
  const isServiceCaseVehicleError = isServiceCaseError;

  const isCheckinPhotosLoading =
    isServiceCaseVehicleLoading || isCheckinLoading || isCheckinAuditLoading;
  const isCheckinPhotosError = isServiceCaseVehicleError || isCheckinError || isCheckinAuditError;

  const isHandoverPhotosLoading =
    isServiceCaseVehicleLoading || isHandoverLoading || isHandoverAuditLoading;
  const isHandoverPhotosError =
    isServiceCaseVehicleError || isHandoverError || isHandoverAuditError;

  return (
    <Main>
      <VStack spacing={4}>
        <DataStatus
          isLoading={isServiceCaseVehicleLoading}
          isError={isServiceCaseVehicleError}
          minHeight={43}
        >
          <VehicleAlbumGallery
            data-testid={testIds.workshop.serviceCaseDetail('serviceCasePhotos')}
            vehicleId={serviceCase?.vehicleId ?? ''}
            serviceCaseId={serviceCaseId}
            enableCreate
          />
        </DataStatus>
        <Card
          title={i18n.t('entity.workshop.labels.checkIn')}
          actions={[
            {
              type: 'button',
              variant: 'link',
              title: i18n.t('entity.workshop.actions.openCheckin'),
              leftIcon: 'image/remove_red_eye',
              onClick: () => {
                setSearchParams(
                  {section: workshopSearchParams.serviceDetail.checkinHandover},
                  {replace: true}
                );
              },
            },
            {
              type: 'button',
              variant: 'link',
              title: i18n.t('general.actions.downloadAll'),
              leftIcon: 'file/download',
              onClick: () => {
                handleInspectionGalleryBatchDownload(checkinAudit?.[0]?.id);
              },
              isDisabled: !checkinPhotos.length,
            },
          ]}
          data-testid={testIds.workshop.serviceCaseDetail('checkinPhotos')}
        >
          <DataStatus
            isLoading={isCheckinPhotosLoading}
            isError={isCheckinPhotosError}
            minHeight={24}
          >
            <VStack spacing={4}>
              <PhotoGrid
                images={checkinPhotos}
                onSlideClick={(_, index) => onCheckinPhotoOpen(index)}
                data-testid={testIds.workshop.serviceCaseDetail('checkinPhotos')}
              />
              <HStack wrap spacing={4}>
                {checkinVideos.map((video, index) => (
                  <Box key={video.id} width={58} height={43}>
                    <VideoPreview
                      videoAsset={video}
                      isLoading={!isVideoPlayable(video)}
                      onOpenPreview={() =>
                        onCheckinPhotoOpen(
                          getPrecedingPlayableVideos(checkinVideos, index) + checkinPhotos.length
                        )
                      }
                      data-testid={testIds.workshop.serviceCaseDetail('checkinVideo')}
                    />
                  </Box>
                ))}
              </HStack>
            </VStack>
            <Lightbox
              data-testid={testIds.workshop.serviceCaseDetail('checkinPhotos')}
              controls={checkinPhotosLightboxControls}
              images={makeLightboxPhotos(checkinPhotos)}
              videos={makeLightboxVideos(checkinVideos)}
            />
          </DataStatus>
        </Card>
        <Card
          title={i18n.t('entity.workshop.labels.handover')}
          actions={[
            {
              type: 'button',
              variant: 'link',
              title: i18n.t('entity.workshop.actions.openHandover'),
              leftIcon: 'image/remove_red_eye',
              onClick: () => {
                setSearchParams(
                  {
                    section: workshopSearchParams.serviceDetail.checkinHandover,
                    checkinHandover: HANDOVER_SECTION,
                  },
                  {replace: true}
                );
              },
            },
            {
              type: 'button',
              variant: 'link',
              title: i18n.t('general.actions.downloadAll'),
              leftIcon: 'file/download',
              onClick: () => {
                handleInspectionGalleryBatchDownload(handoverAudit?.[0]?.id);
              },
              isDisabled: !handoverPhotos.length,
            },
          ]}
          data-testid={testIds.workshop.serviceCaseDetail('handoverPhotos')}
        >
          <DataStatus
            isLoading={isHandoverPhotosLoading}
            isError={isHandoverPhotosError}
            minHeight={24}
          >
            <VStack spacing={4}>
              <PhotoGrid
                images={handoverPhotos}
                onSlideClick={(_, index) => onHandoverPhotoOpen(index)}
                data-testid={testIds.workshop.serviceCaseDetail('handoverPhotos')}
              />
              <HStack wrap spacing={4}>
                {handoverVideos.map((video, index) => (
                  <Box key={video.id} width={58} height={43}>
                    <VideoPreview
                      videoAsset={video}
                      isLoading={!isVideoPlayable(video)}
                      onOpenPreview={() =>
                        onHandoverPhotoOpen(
                          getPrecedingPlayableVideos(handoverVideos, index) + handoverPhotos.length
                        )
                      }
                      data-testid={testIds.workshop.serviceCaseDetail('handoverVideo')}
                    />
                  </Box>
                ))}
              </HStack>
            </VStack>
            <Lightbox
              data-testid={testIds.workshop.serviceCaseDetail('handoverPhotos')}
              controls={handoverPhotosLightboxControls}
              images={makeLightboxPhotos(handoverPhotos)}
              videos={makeLightboxVideos(handoverVideos)}
            />
          </DataStatus>
        </Card>
        <ServiceCasePhotos
          vehicleId={serviceCase?.vehicleId ?? ''}
          ignoredServiceCaseId={serviceCaseId}
          behindButton
        />
      </VStack>
      {lightboxActionsFactory.photoDownloadSizeDialog}
    </Main>
  );
}
